import { RotateCcw, AlertTriangle, X } from "lucide-react";
import { useTranslation } from "../../i18n";
import type { DispatchOrder } from "../../types/Dispatch";
import type { UnitReopenInfo } from "./dispatch-scan-tote";

// Confirmation for reopening an ALREADY-SHIPPED order that the operator
// pulled up by scanning its shipping label.
//
// Distinct from dispatch-reopen-button's modal: that one reopens the order
// this desk just shipped (boxes still packed, still on screen), this one
// reopens an order that left the desk — so the warning has to spell out
// what reopening actually destroys. `reopen.pastCutoff` is the one the
// operator MUST read, worded per carrier via `pastCutoffReason`:
//   'manifested'      APC — past the manifest cutoff APC refuses the void,
//                     so the label stays live and billable and someone has
//                     to chase a credit
//   'already_scanned' InPost — a parcel has been scanned into their
//                     network, so it is travelling and billed (InPost voids
//                     are local; nothing to refuse, nothing to credit)
type Props = {
    order: DispatchOrder;
    reopen: UnitReopenInfo;
    busy: boolean;
    onConfirm: () => void;
    onCancel: () => void;
};

const DispatchLabelReopenModal = ({ order, reopen, busy, onConfirm, onCancel }: Props) => {
    const { t } = useTranslation();

    return (
        <div className="modal-overlay" onClick={busy ? undefined : onCancel}>
            <div className="modal-card" onClick={e => e.stopPropagation()}>
                <div className="modal-card-header">
                    <h2>{t("reopen_order")}</h2>
                    <button className="modal-close" onClick={onCancel} disabled={busy}><X size={18} /></button>
                </div>
                <div className="modal-card-body">
                    <div className="dispatch-reopen-warning-icon">
                        <AlertTriangle size={32} />
                    </div>
                    <p className="dispatch-reopen-warning-order">#{order.order_number}</p>
                    <p className="dispatch-reopen-warning-text">
                        {reopen.labelCount > 0
                            ? t("dispatch_label_reopen_warning", { n: reopen.labelCount })
                            : t("dispatch_label_reopen_warning_no_labels")}
                    </p>
                    {reopen.pastCutoff && (
                        <p className="dispatch-reopen-warning-text dispatch-reopen-warning-danger">
                            {reopen.pastCutoffReason === "already_scanned"
                                ? t("dispatch_label_reopen_already_scanned")
                                : t("dispatch_label_reopen_past_cutoff", { cutoff: reopen.cutoff ?? "" })}
                        </p>
                    )}
                    <div className="modal-confirm-buttons" style={{ marginTop: 20 }}>
                        <button className="btn btn-ghost btn-lg" onClick={onCancel} disabled={busy}>
                            {t("cancel")}
                        </button>
                        <button className="btn btn-danger btn-lg" onClick={onConfirm} disabled={busy}>
                            <RotateCcw size={22} />
                            {t("reopen_order")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DispatchLabelReopenModal;
